import React, { useState } from "react";

import { API, graphqlOperation } from "aws-amplify";
import { createService, updateService } from "../../../graphql/mutations";

import { countBy, isEmpty, keyBy, size } from "lodash";

import {
  Backdrop,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  makeStyles,
  Typography,
} from "@material-ui/core";

import {
  ArrayField,
  Button,
  Datagrid,
  FileInput,
  TextField,
  useNotify,
  useRedirect,
} from "react-admin";
import { useForm } from "react-final-form";

import { AttachFileIcon, ImportIcon } from "../../../components/common/iconography";
import { FileField } from "../../../components/common/FileField";
import { logError, logHistory } from "../../../utils/appUtils";
import {
  MAX_FILE_SIZE,
  MAX_FILE_SIZE_MSG,
  ServiceHeaderMap,
  UnitOptions,
} from "../../../utils/constants";
import { csv2arr } from "../../../utils/fileUtils";
import { CancelIcon, CloseIcon } from "../../../components/common/iconography";
import { ResourceType } from "../../../utils/constants";
import { composeNewService, getDefaultTaxRate } from "./ServiceUtils";

const useStyles = makeStyles((theme) => ({
  scrollableGrid: {
    overflowX: "auto",
    overflowY: "hidden",
    width: "100%",
    border: "1px solid #ccc",
    borderRadius: 4,
    paddingBottom: 8,
    "&::-webkit-scrollbar": {
      height: "8px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#888",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: "#555",
    },
  },
  dialogContent: {
    height: "90vh",
  },
  dialogTitle: {
    display: "flex",
    paddingTop: "5px",
    paddingBottom: "5px",
    justifyContent: "space-between",
  },
}));

const validateCSV = (csvArray, errors) => {
  let missingName = false;
  let invalidTaxRate = false;
  let negativeTaxRate = false;
  let invalidTaxCode = false;

  const dupNames = Object.entries(countBy(csvArray, "name")).reduce(
    (acc, [key, val]) => {
      return key !== "" && val > 1 ? acc.concat(key) : acc;
    },
    [],
  );
  if (dupNames?.length > 0)
    errors.push(
      `Duplicate "Service Name" entries: ${dupNames.map((dup) => ` ${dup}`)}`,
    );

  csvArray.forEach((row) => {
    // check for duplicates & missing names
    row.error = row.error || dupNames.some((dup) => dup === row["name"]);
    if (row["name"] === "") {
      missingName = true;
      row.error = true;
    }

    // check tax rate
    const taxRate = Number(row["taxRate"]);
    if (isNaN(taxRate)) {
      row.error = true;
      invalidTaxRate = true;
    } else if (taxRate < 0) {
      negativeTaxRate = true;
    }

    // check tax code
    if (row["taxCode"] && row["taxCode"].trim() !== "") {
      const validTaxCodes =
        window.appUser?.reloadYard?.taxRates?.map((rate) => rate.taxCode) || [];
      if (!validTaxCodes.includes(row["taxCode"])) {
        row.error = true;
        invalidTaxCode = true;
      }
    }

    // check all unit rate fields for numeric >= 0
    UnitOptions.forEach((unit) => {
      const rate = Number(row[unit.column]);
      if (row[unit.column] !== "" && (isNaN(rate) || rate < 0)) {
        row.error = true;
      }
    });
  });

  if (missingName) errors.push(`Missing "Service Name" entries.`);

  if (invalidTaxRate) errors.push(`Invalid "Tax Rate %" entries.`);
  if (negativeTaxRate) errors.push(`"Tax Rate %" must be 0 or greater.`);
  if (invalidTaxCode) errors.push(`Invalid "Tax Code" entries.`);

  return errors;
};

const processCSV = async (
  str,
  delim = ",",
  setCSVData,
  setCSVErrors,
  notify,
) => {
  let errors = [];
  let lengthError = false;

  try {
    let headers = str
      .slice(0, str.indexOf("\n"))
      .replace("\r", "")
      .split(delim);
    headers = headers.filter((head) => head !== "");
    const hlen = headers.length;

    let columnCount = 0;
    let headerColumns = [];
    headers.forEach((h) => {
      const col = ServiceHeaderMap[h.trim()]; // Trim header names
      if (col) {
        headerColumns.push(col);
        columnCount++;
      }
    });

    const invalidHeaders = columnCount !== size(ServiceHeaderMap);
    if (invalidHeaders) {
      notify("Modified or unrecognized column headers.", "warning");
      setCSVErrors(["Modified or unrecognized column headers."]);
      setCSVData({});
      return;
    }

    let rows = csv2arr(str.slice(str.indexOf("\n") + 1));
    rows = rows.filter((row) => row.join("") !== "");

    let newArray = rows
      .map((row) => {
        if (!isEmpty(row) && row.length > 1) {
          const eachObject = headerColumns.reduce((obj, header, i) => {
            obj[header] = row[i].trim();
            return obj;
          }, {});

          if (row.length > hlen) {
            eachObject["error"] = true;
            lengthError = true;
          }

          return eachObject;
        }
        return null;
      })
      .filter((itm) => itm);

    if (lengthError)
      errors.push(
        `Values in extra columns, or extra unquoted commas within entries.`,
      );
    errors = validateCSV(newArray, errors);
    if (errors.length > 0) {
      setCSVErrors(errors);
      newArray = newArray.filter((row) => row.error);
      setCSVData({ preview: newArray.slice(0, 10) });
    } else {
      setCSVData({ services: newArray, preview: newArray.slice(0, 10) });
      notify("Services u;loaded successfully.", "info");
    }
  } catch (err) {
    logError("services:ServiceUpload:processCSV", err, notify);
  }
};

const ErrorTextField = (props) => (
  <TextField color={props.record.error ? "error" : "body"} {...props} />
);

export const ServiceUpload = ({
  csvData,
  setCSVData,
  csvErrors,
  setCSVErrors,
  hidePreviewIfNoErrors,
  ...props
}) => {
  const classes = useStyles();
  const form = useForm();
  const notify = useNotify();

  const handleCSVFile = async (value) => {
    try {
      setCSVErrors(null);
      if (!value) {
        form.change("servicesFile", null);
        setCSVData({});
      } else if (value?.size > MAX_FILE_SIZE) {
        notify(MAX_FILE_SIZE_MSG, "warning");
        form.change("servicesFile", null);
        setCSVData({});
      } else {
        const fileState = form.getFieldState("servicesFile");
        const reader = new FileReader();
        reader.onload = function (e) {
          const text = e.target.result;
          processCSV(text, ",", setCSVData, setCSVErrors, notify);
        };
        let blob = await fetch(fileState.value.src).then((r) => r.blob());
        reader.readAsText(blob);
      }
    } catch (error) {
      logError("services:ServiceUpload:handleCSVFile", error, notify);
    }
  };

  return (
    <Grid container direction="column" spacing={1} fullWidth>
      <Grid container direction="row" spacing={4}>
        <Grid item xs={12} md={6}>
          <Typography variant="overline" color="textSecondary" gutterBottom>
            Instructions
          </Typography>
          <ol>
            <li>Export existing Services from another account.</li>
            <li>Do not modify the column headers or the data.</li>
            <li>Upload the CSV file here.</li>
            <li>For help, contact us at 877-TRANSLOAD / 877-872-6756</li>
          </ol>
        </Grid>
        <Grid item xs={12} md={6}>
          <FileInput
            source="servicesFile"
            label="SERVICES IMPORT"
            variant="outlined"
            fullWidth
            placeholder={
              <p>
                <AttachFileIcon fontSize="large" /> <br /> Drop a CSV File to
                upload, or click to select it.
              </p>
            }
            accept=".csv"
            onChange={handleCSVFile}
            helperText={false}
          >
            <FileField source="src" title="title" target="_blank" />
          </FileInput>
        </Grid>
      </Grid>
      <Grid container direction="row" spacing={1}>
        {csvErrors && (
          <Grid item xs={12}>
            <Typography variant="overline" color="error">
              Loading Errors:
            </Typography>
            <ul>
              {csvErrors.map((error, index) => (
                <Typography key={index} color="error">
                  <li>{error}</li>
                </Typography>
              ))}
            </ul>
          </Grid>
        )}
        {csvData?.preview?.length > 0 &&
          !(hidePreviewIfNoErrors && !csvErrors) && (
            <>
              <Grid item xs={12}>
                <Typography
                  variant="overline"
                  color="textSecondary"
                  gutterBottom
                >
                  Upload Preview (top {`${csvData.preview.length}`} rows):
                </Typography>
              </Grid>
              <Grid item xs={12} className={classes.scrollableGrid}>
                <ArrayField source="preview" record={csvData} fullWidth>
                  <Datagrid>
                    <ErrorTextField source="name" label="Service Name" />
                    <ErrorTextField source="description" label="Description" />
                    <ErrorTextField source="status" label="Status" />
                    <ErrorTextField source="taxCode" label="Tax Code" />
                    <ErrorTextField source="taxRate" label="Tax Rate %" />
                    {UnitOptions.map((unit) => (
                      <ErrorTextField
                        source={unit.column}
                        label={unit.column}
                      />
                    ))}
                  </Datagrid>
                </ArrayField>
              </Grid>
            </>
          )}
      </Grid>
    </Grid>
  );
};

export const ServiceUploadDialog = ({
  account,
  existingServices,
  uploadOpen,
  setUploadOpen,
  handleRefresh,
  ...props
}) => {
  const classes = useStyles();
  const form = useForm();
  const notify = useNotify();
  const redirect = useRedirect();
  const [csvData, setCSVData] = useState({});
  const [csvErrors, setCSVErrors] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const handleServicesUpload = async () => {
    debugger;
    if (csvData.services?.length > 0) {
      try {
        setIsSaving(true);
        const defaultTaxRate = getDefaultTaxRate(window.appUser?.reloadYard);
        const oldServicesMap =
          existingServices?.length > 0 ? keyBy(existingServices, "name") : {};

        const results = await Promise.allSettled(
          csvData.services.map((item) => {
            // check old vs new services
            const oldService = oldServicesMap[item["name"]];
            if (oldService) {
              const input = { ...oldService, ...item };

              // Recombine tax rate object from separate columns
              const taxRateValue = parseFloat(item["taxRate"]) || 0;
              const taxCode = item["taxCode"] || "";

              if (taxCode) {
                const taxRateObject =
                  window.appUser?.reloadYard?.taxRates?.find(
                    (rate) =>
                      rate.taxCode === taxCode && rate.taxRate === taxRateValue,
                  );
                input.taxRate = taxRateObject || defaultTaxRate;
              } else {
                input.taxRate = defaultTaxRate;
              }

              // Parse unit rate fields
              UnitOptions.forEach((unit) => {
                const unitValue = item[unit.column];
                input[unit.column] = unitValue ? parseFloat(unitValue) : 0;
              });

              // History will be logged via logHistory after successful update

              delete input.createdAt;
              delete input.error;
              delete input.history;
              delete input.updatedAt;

              // cleanup side-effects
              delete input.taxCode;

              return API.graphql(graphqlOperation(updateService, { input }));
            } else {
              // History will be logged via logHistory after successful creation
              const input = composeNewService(item, account);
              return API.graphql(graphqlOperation(createService, { input }));
            }
          }),
        );

        const resError = results.find((itm) => itm.status === "rejected");
        if (resError) throw resError.reason;

        const newServices = [];
        const updatedServiceMap = {};
        results.forEach(
          (result) =>
            result.value.data.createService &&
            newServices.push(result.value.data.createService),
        );
        results.forEach((result) => {
          if (result.value.data.updateService)
            updatedServiceMap[result.value.data.updateService.id] =
              result.value.data.updateService;
        });

        let newServiceList = existingServices.map((itm) =>
          updatedServiceMap[itm.id] ? updatedServiceMap[itm.id] : itm,
        );
        newServiceList = [...newServiceList, ...newServices];
        form.change("services.items", newServiceList);

        // Log history for successfully created and updated services
        for (const service of newServices) {
          const historyNote = {
            parentId: service.id,
            parentType: ResourceType.SERVICE,
            text: "Service Created.",
          };
          logHistory(historyNote);
        }

        for (const service of Object.values(updatedServiceMap)) {
          const historyNote = {
            parentId: service.id,
            parentType: ResourceType.SERVICE,
            text: "Services bulk update.",
          };
          logHistory(historyNote);
        }

        notify("Services imported successfully", "success");
      } catch (error) {
        logError(
          "services:ServiceUpload:handleServicesUpload",
          error,
          notify,
          redirect,
        );
      } finally {
        handleRefresh && handleRefresh();
        setIsSaving(false);
      }
    }
    handleClose();
  };

  const handleClose = () => {
    setCSVData({});
    setCSVErrors(null);
    setUploadOpen(false);
  };

  return (
    <Dialog maxWidth="xl" open={uploadOpen} fullWidth>
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <h2> Services Import </h2>
        <IconButton aria-label="close" onClick={handleClose}>
          <CloseIcon fontSize="large" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers className={classes.dialogContent}>
        <Backdrop className={classes.backdrop} open={isSaving}>
          <CircularProgress color="inherit" />
        </Backdrop>
        <ServiceUpload
          csvData={csvData}
          setCSVData={setCSVData}
          csvErrors={csvErrors}
          setCSVErrors={setCSVErrors}
        />
      </DialogContent>
      <DialogActions>
        <Button label="Cancel" color="default" autoFocus onClick={handleClose}>
          <CancelIcon />
        </Button>
        <Button
          label="Import"
          color="primary"
          onClick={handleServicesUpload}
          disabled={csvErrors || isSaving}
        >
          <ImportIcon />
        </Button>
      </DialogActions>
    </Dialog>
  );
};
